import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { TableCell, TableRow, cn } from '@shigo-ui/ui';

import {
  type DashboardDataTableColumn,
  type DashboardDataTableRowLink,
} from './DashboardDataTable.types';
import { DashboardTableNameCell } from './DashboardTableNameCell/DashboardTableNameCell';
import { isDashboardDataTableNameColumn } from './DashboardDataTableSticky';
import { DashboardDataTableLoading } from './DashboardDataTableLoading';
import { DashboardDataTableMessage } from './DashboardDataTableMessage';
import {
  isDashboardDataTableSelectionColumn,
  dashboardDataTableStickyCellClassName,
  dashboardDataTableStickyCellStyle,
} from './DashboardDataTableSticky';

export function DashboardDataTableRows<TItem>({
  columnCount,
  columns,
  emptyMessage,
  errorMessage,
  getRowKey,
  items,
  loading,
  rowLink,
}: {
  columnCount: number;
  columns: DashboardDataTableColumn<TItem>[];
  emptyMessage: ReactNode;
  errorMessage: ReactNode | undefined;
  getRowKey: (item: TItem) => string;
  items: TItem[];
  loading: boolean;
  rowLink: DashboardDataTableRowLink<TItem> | undefined;
}) {
  if (loading) {
    return <DashboardDataTableLoading columns={columns} />;
  }

  if (errorMessage) {
    return <DashboardDataTableMessage columnCount={columnCount} message={errorMessage} />;
  }

  if (!items.length) {
    return <DashboardDataTableMessage columnCount={columnCount} message={emptyMessage} />;
  }

  return items.map((item) => {
    const href = rowLink?.getHref(item);

    return (
      <TableRow
        key={getRowKey(item)}
        role={href ? 'link' : undefined}
        tabIndex={href ? 0 : undefined}
        aria-label={href ? rowLink?.getLabel?.(item) : undefined}
        data-href={href}
        className={cn(
          'group border-border hover:bg-muted/40 transition-colors',
          href &&
            'focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none',
        )}
        onAuxClick={
          href && rowLink
            ? (event) => {
                handleRowAuxClick(event, item, href, rowLink);
              }
            : undefined
        }
        onClick={
          href && rowLink
            ? (event) => {
                handleRowClick(event, item, href, rowLink);
              }
            : undefined
        }
        onKeyDown={
          href && rowLink
            ? (event) => {
                handleRowKeyDown(event, item, href, rowLink);
              }
            : undefined
        }
      >
        {columns.map((column) => {
          let content = column.cell(item);
          if (isDashboardDataTableNameColumn(column.id)) {
            content = <DashboardTableNameCell>{content}</DashboardTableNameCell>;
          } else if (isDashboardDataTableSelectionColumn(column.id)) {
            content = <div className="flex items-center justify-center">{content}</div>;
          }
          return (
            <TableCell
              key={column.id}
              data-selection-column={isDashboardDataTableSelectionColumn(column.id) || undefined}
              className={cn(
                // In-cell controls (icon buttons, counts) keep their size and hit area but do
                // not stretch the compact row: their extra height overlaps the cell padding.
                'px-2 py-2.5 first:pl-4 last:pr-4 [&_a.inline-flex]:-my-2 [&_button]:-my-2',
                // On phones the name text must not set the column's minimum width, so the
                // sticky-columns hook's fixed 45% width holds and long names truncate.
                isDashboardDataTableNameColumn(column.id) && 'max-sm:max-w-0',
                column.className,
                dashboardDataTableStickyCellClassName(column),
              )}
              style={dashboardDataTableStickyCellStyle(column)}
            >
              {content}
            </TableCell>
          );
        })}
      </TableRow>
    );
  });
}

function handleRowClick<TItem>(
  event: MouseEvent<HTMLTableRowElement>,
  item: TItem,
  href: string,
  rowLink: DashboardDataTableRowLink<TItem>,
) {
  if (isInteractiveEventTarget(event.target, event.currentTarget)) {
    return;
  }

  if (shouldOpenHrefInNewTab(event)) {
    event.preventDefault();
    openHrefInNewTab(href);
    return;
  }

  rowLink.onNavigate?.(href, item, event);
  if (!rowLink.onNavigate) {
    window.location.assign(href);
  }
}

function handleRowAuxClick<TItem>(
  event: MouseEvent<HTMLTableRowElement>,
  _item: TItem,
  href: string,
  _rowLink: DashboardDataTableRowLink<TItem>,
) {
  if (event.button !== 1 || isInteractiveEventTarget(event.target, event.currentTarget)) {
    return;
  }

  event.preventDefault();
  openHrefInNewTab(href);
}

function handleRowKeyDown<TItem>(
  event: KeyboardEvent<HTMLTableRowElement>,
  item: TItem,
  href: string,
  rowLink: DashboardDataTableRowLink<TItem>,
) {
  if (event.key !== 'Enter' || isInteractiveEventTarget(event.target, event.currentTarget)) {
    return;
  }

  event.preventDefault();
  rowLink.onNavigate?.(href, item);
  if (!rowLink.onNavigate) {
    window.location.assign(href);
  }
}

function shouldOpenHrefInNewTab(event: MouseEvent<HTMLTableRowElement>) {
  return event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

function openHrefInNewTab(href: string) {
  window.open(href, '_blank', 'noopener,noreferrer');
}

function isInteractiveEventTarget(target: EventTarget | null, currentTarget: HTMLTableRowElement) {
  if (!(target instanceof Element)) {
    return false;
  }

  const interactiveElement = target.closest(
    'a,button,input,textarea,select,summary,[data-dashboard-table-interactive="true"]',
  );

  return Boolean(interactiveElement && currentTarget.contains(interactiveElement));
}
